import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import axios from 'axios'
import GameSearchBar from '../components/GameSearchBar'
import ReviewCard from '../components/ReviewCard'
import './HomePage.css'

export default function HomePage({ user }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [myReviews, setMyReviews] = useState([])
  const [communityReviews, setCommunityReviews] = useState([])
  const [favorites, setFavorites] = useState([])
  const [loading, setLoading] = useState(true)

  // Get search query from URL if present
  const searchQuery = searchParams.get('search') || ''

  useEffect(() => {
    loadReviews()
    loadFavorites()
  }, [])

  const loadReviews = async () => {
    try {
      const [myResponse, communityResponse] = await Promise.all([
        axios.get('/api/reviews/mine', { withCredentials: true }),
        axios.get('/api/reviews/community', { withCredentials: true })
      ])
      setMyReviews(myResponse.data)
      setCommunityReviews(communityResponse.data)
    } catch (error) {
      console.error('Failed to load reviews:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadFavorites = async () => {
    try {
      const response = await axios.get('/api/favorites', { withCredentials: true })
      // Show only the last 5 favorites for quick access
      setFavorites(response.data.slice(0, 5))
    } catch (error) {
      console.error('Failed to load favorites:', error)
    }
  }

  const handleDeleteReview = async (reviewId) => {
    try {
      await axios.delete(`/api/reviews/${reviewId}`, { withCredentials: true })
      setMyReviews(myReviews.filter(r => r.review_id !== reviewId))
    } catch (error) {
      console.error('Failed to delete review:', error)
      alert('Failed to delete review. Please try again.')
    }
  }

  return (
    <motion.div
      className="page-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <div className="container">
        <div className="home-hero">
          <h1 className="page-title">
            Welcome back, {user.display_name}! 🎮
          </h1>
          <p className="home-subtitle">
            Search for games, write reviews, and level up!
          </p>
        </div>

        <div className="home-search">
          <GameSearchBar autoFocus initialQuery={searchQuery} />
        </div>

        {favorites.length > 0 && (
          <div className="home-favorites">
            <h3 className="favorites-quick-title">⭐ Quick Access</h3>
            <div className="favorites-quick-grid">
              {favorites.map((fav) => (
                <Link
                  key={fav.favorite_id}
                  to={`/game/${fav.game_id}`}
                  style={{ textDecoration: 'none' }}
                >
                  <motion.div
                    className="favorite-quick-btn"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {fav.game_cover_url && (
                      <img
                        src={fav.game_cover_url}
                        alt={fav.game_name}
                        className="favorite-quick-img"
                      />
                    )}
                    <div className="favorite-quick-name">{fav.game_name}</div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="home-stats">
          <div className="stat-card">
            <div className="stat-value">{user.review_count || 0}</div>
            <div className="stat-label">Reviews Written</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{user.current_level || 1}</div>
            <div className="stat-label">Current Level</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{user.total_xp || 0}</div>
            <div className="stat-label">Total XP</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{user.friend_count || 0}</div>
            <div className="stat-label">Friends</div>
          </div>
        </div>

        {loading ? (
          <div className="home-loading">
            <div className="spinner"></div>
            <p>Loading reviews...</p>
          </div>
        ) : (
          <>
            {/* My Reviews Section */}
            <div className="home-reviews">
              <h2 className="section-title">Your Reviews 📝</h2>
              {myReviews.length > 0 ? (
                <div className="reviews-grid">
                  {myReviews.map((review) => (
                    <ReviewCard
                      key={review.review_id}
                      review={review}
                      currentUserId={user.user_id}
                      onDelete={handleDeleteReview}
                    />
                  ))}
                </div>
              ) : (
                <div className="home-empty-section">
                  <p>You haven't written any reviews yet!</p>
                  <p>Search for a game above and write your first review to start leveling up!</p>
                </div>
              )}
            </div>

            {/* Community Reviews Section */}
            {communityReviews.length > 0 && (
              <div className="home-reviews community-section">
                <h2 className="section-title">Community Reviews 🎮</h2>
                <div className="reviews-grid">
                  {communityReviews.map((review) => (
                    <ReviewCard
                      key={review.review_id}
                      review={review}
                      currentUserId={user.user_id}
                      onDelete={null}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
  )
}
